<script setup lang="ts">
import { onHide, onLaunch, onPageNotFound, onShow } from '@dcloudio/uni-app';
import { ref } from 'vue';
import { OLD_ROUTE_MAP } from '@/router/config';
import { checkPcRedirect } from '@/service/pc-redirect';
import { initPrivacyGate, waitForPrivacyConsent } from '@/service/privacy';
import { pushService } from '@/service/push';
import { handleRechargePay, saveRechargePayFromLaunchOptions } from '@/service/recharge-pay';
import { useAppStore } from '@/store';
import { useThemeStore } from '@/store/modules/theme';
import { getSkin } from '@/themes/skins';
import { resolveSkinCode } from '@/themes/useTheme';
import { initAnalytics, trackPageEnd, trackPageStart } from '@/utils/analytics';
import { setupModal, showLoading } from '@/utils/modal';

// 初始化 Store
const appStore = useAppStore();
// 标记是否已经处理过完整的初始化流程 (防止重复)
const hasProcessedFullInit = ref(false);

// 注册推送监听（不阻塞应用启动）
const initPushListener = () => {
  // H5端 uni.getPushClientId 可能不触发回调，不使用 await 避免阻塞初始化
  pushService.init().catch((err) => {
    console.error('[Push] 初始化监听失败', err);
  });
};

// 解析启动参数中的路径和查询参数
const parseLaunchOptions = (options: any) => {
  let targetPath = options?.path || '';
  const targetQuery = { ...(options?.query || {}) };

  // H5 端：补齐解析路径与参数（onLaunch 时 options.path/query 可能不完整）

  const hash = window.location.hash;
  if (hash && hash.length > 1) {
    // hash 模式：从 hash 解析
    const hashPath = hash.slice(1);
    if (hashPath) {
      targetPath = hashPath.split('?')[0];
      const hashQuery = hashPath.split('?')[1];
      if (hashQuery) {
        const params = new URLSearchParams(hashQuery);
        params.forEach((value, key) => {
          targetQuery[key] = value;
        });
      }
    }
  }
  else {
    // history 模式：从 pathname 解析路径
    const pathname = window.location.pathname;
    const basePath = import.meta.env.BASE_URL || '/';
    if (pathname && pathname !== '/') {
      // 去除 base 路径前缀
      const relativePath = pathname.startsWith(basePath)
        ? pathname.slice(basePath.length - 1)
        : pathname;
      if (relativePath && relativePath !== '/') {
        targetPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
      }
    }
  }
  // 无论 hash 还是 history 模式，都从 search 补充 query 参数
  const search = window.location.search;
  if (search && search.length > 1) {
    const params = new URLSearchParams(search.slice(1));
    params.forEach((value, key) => {
      targetQuery[key] = value;
    });
  }


  // 确保路径以/开头
  if (targetPath && !targetPath.startsWith('/')) {
    targetPath = `/${targetPath}`;
  }

  // 兼容 Vue2 旧路由映射 -> 重定向到 Vue3 对应页面
  if (OLD_ROUTE_MAP[targetPath]) {
    console.warn(`[App] Vue2 旧路由 ${targetPath} -> ${OLD_ROUTE_MAP[targetPath]}`);
    targetPath = OLD_ROUTE_MAP[targetPath];
  }

  return { targetPath, targetQuery };
};

// 皮肤配色合并（阶段 4）：皮肤声明 colors 时浅合入 themeVars；无皮肤空跑。
// 调用点放 App.vue 避免 theme store 反向 import themes 形成循环依赖。
const applySkinColors = () => {
  const skin = getSkin(resolveSkinCode());
  if (!skin?.colors) return;
  const themeStore = useThemeStore();
  themeStore.setThemeVars({ ...themeStore.themeVars, ...skin.colors });
};

// 处理核心初始化逻辑 (隐私同意后执行)
const performFullInitialization = async () => {
  if (hasProcessedFullInit.value) return;

  try {
    showLoading();
    // 1. 加载项目配置
    await appStore.fetchConfig();

    // 1.0 检测是否 PC 端浏览器，是则跳转至 PC 端页面
    checkPcRedirect(appStore.pcUrl);

    // 1.1 初始化百度统计 (H5 端需要 baiduHmUrl，App/小程序用环境变量)
    initAnalytics({ baiduHmUrl: appStore.configData.baiduHmUrl });

    // 1.2 应用皮肤配色（阶段 4；initTheme 已在 onLaunch 完成，此处叠加皮肤差异）
    applySkinColors();

    // 2. 加载项目其他配置，
    await appStore.fetchOtherConfig();

    // 3. 检查版本更新
    await appStore.checkVersion();

    // 4. 处理PC扫码登录验证
    await appStore.verifyScanLogin();

    // 5. 数据加载完成，标记初始化完成
    hasProcessedFullInit.value = true;
    uni.hideLoading();

    // 6. 处理外部扫码充值支付（必须在配置加载完成后执行）
    await handleRechargePay();

    // 注意：页面跳转统一由 index.vue (启动页) 控制
    // 这里只负责完成配置加载，index.vue 会监听 isConfigLoaded 状态并执行跳转
  }
  catch (error) {
    // console.error('Initialization failed', error);
    uni.hideLoading();
    uni.reLaunch({ url: '/pages/error/404?error=init_failed' });
  }
};

onLaunch(async (options) => {
  console.log('App.vue onLaunch', options);

  // 0. 初始化配置就绪 Promise（必须在 parseLaunchOptions 之前，确保页面 onLoad 可以等待）
  appStore.initConfigReadyPromise();

  // 1. 保存启动参数
  const { targetPath, targetQuery } = parseLaunchOptions(options);
  appStore.setLaunchOptions({
    ...options,
    path: targetPath,
    query: targetQuery,
  });

  // 1.1 检测外部扫码充值支付参数（必须在初始化之前保存，以便登录后能读取）
  saveRechargePayFromLaunchOptions(targetQuery);

  // 2. 恢复本地状态
  appStore.initLocalState();

  // 3. 注册全局modal（useMessage和useTotal）
  setupModal();

  // 4. 首启隐私同意闸门（iOS 工作台包）：未同意前不初始化推送、不加载项目配置；
  //    弹窗由 App.ku.vue 按 privacyStatus === 'pending' 渲染，点「不同意」直接退出应用
  initPrivacyGate();
  await waitForPrivacyConsent();

  // 5. 注册 uniPush 消息监听（不阻塞初始化流程）
  initPushListener();

  // 6. 执行完整初始化（加载项目配置）
  await performFullInitialization();
});
onShow(async (options) => {
  // console.log('App.vue onShow', options);

  // App 端：记录应用前台显示时的页面








  // 小程序端：应用从后台切到前台时上报当前页面 PV（uni-app 不使用全局 Page()，需手动触发）





});
onHide(() => {
  // console.log('App Hide');
  // 清除角标
  pushService.clearBadge();

  // App 端：记录应用进入后台时的页面结束








  // 小程序端：应用切到后台时上报页面 hide






});

// 页面不存在时（H5 history 模式直接访问旧 URL），检查是否为 Vue2 旧路由，是则重定向
onPageNotFound((options) => {
  const { path } = options;
  const newPath = OLD_ROUTE_MAP[path];

  if (newPath) {
    // 场景一：已知 Vue2 旧路由 → 先切到首页保证页面栈，再从首页 navigateTo 到目标页，保留返回键
    console.warn(`[App] Vue2 旧路由 ${path} -> ${newPath}`);

    const search = window.location.search;
    const targetUrl = search ? `${newPath}${search}` : newPath;
    uni.switchTab({
      url: '/pages/tabbar/home/home',
      success() {
        uni.navigateTo({ url: targetUrl });
      },
      fail() {
        // switchTab 失败时降级
        uni.redirectTo({ url: targetUrl });
      },
    });




  }
  else {
    // 场景二：未知/不存在的页面 → 进入首页，避免白屏
    console.warn(`[App] 未知页面 ${path}，跳转到首页`);

    uni.switchTab({ url: '/pages/tabbar/home/home' });

  }
});
</script>

<style lang="scss">
@import '@/style/index.scss';
</style>
